// Internal-link maps used by RelatedServices (rendered on blog posts)
// and RelatedGuides (rendered on service pages).
//
// Keep keywords lowercase. Matching is substring-based against the blog
// post title + tags + slug, and against the service slug.

export const SERVICE_CATALOG = [
  {
    slug: 'usa-account',
    title: 'USA LLC Formation',
    description: 'Form a Wyoming or Delaware LLC end-to-end with EIN, registered agent, and US bank account setup.',
    keywords: ['llc', 'usa', 'us company', 'wyoming', 'delaware', 'formation', 'incorporate', 'register us', 'mercury', 'bank'],
  },
  {
    slug: 'ein',
    title: 'EIN Registration',
    description: 'Get your Employer Identification Number (US tax ID) without an SSN. Typical turnaround 3–5 days.',
    keywords: ['ein', 'tax id', 'employer identification', 'irs'],
  },
  {
    slug: 'itin',
    title: 'ITIN Application',
    description: 'Obtain your Individual Taxpayer Identification Number for US tax filing without an SSN.',
    keywords: ['itin', 'individual taxpayer', 'tax filing', 'us tax'],
  },
  {
    slug: 'stripe-account',
    title: 'Stripe Account Setup',
    description: 'Open a fully-verified Stripe account for non-US founders — accept payments globally from day one.',
    keywords: ['stripe', 'payment gateway', 'card payments', 'merchant account'],
  },
  {
    slug: 'registered-agent',
    title: 'Registered Agent (US)',
    description: 'Compliant US registered agent service in any state — included free for the first year with our LLC plans.',
    keywords: ['registered agent', 'agent', 'compliance address'],
  },
  {
    slug: 'trademark-registration',
    title: 'US Trademark Registration',
    description: 'Protect your brand with a USPTO trademark filing handled by our partner attorneys.',
    keywords: ['trademark', 'brand', 'tm', 'uspto', 'intellectual property'],
  },
  {
    slug: 'annual-report',
    title: 'Annual Report Filing',
    description: 'Stay compliant with state annual report filings — we handle the paperwork and reminders.',
    keywords: ['annual report', 'state filing', 'compliance'],
  },
  {
    slug: 'boi-report',
    title: 'BOI Report Filing',
    description: 'Beneficial Ownership Information reporting to FinCEN — required for most US LLCs and corporations.',
    keywords: ['boi', 'beneficial ownership', 'fincen', 'reporting'],
  },
  {
    slug: 'bookkeeping',
    title: 'Bookkeeping',
    description: 'Monthly bookkeeping for your US or UK entity — clean books, ready for tax season.',
    keywords: ['bookkeeping', 'accounting', 'books', 'financial'],
  },
  {
    slug: 'foreign-qualification',
    title: 'Foreign Qualification',
    description: 'Register your existing entity to do business in additional US states.',
    keywords: ['foreign qualification', 'multi-state', 'expand'],
  },
]

// Blog topic clusters → which services to surface on blog posts of that topic.
// First match wins; falls back to USA LLC + EIN as the safest default pair.
export const BLOG_TOPIC_TO_SERVICES = [
  { match: ['wise'], services: ['usa-account', 'stripe-account', 'ein'] },
  { match: ['stripe'], services: ['stripe-account', 'usa-account', 'ein'] },
  { match: ['paypal'], services: ['usa-account', 'stripe-account', 'ein'] },
  { match: ['payment gateway', 'gateway', 'merchant'], services: ['stripe-account', 'usa-account'] },
  { match: ['llc', 'wyoming', 'delaware', 'us company'], services: ['usa-account', 'ein', 'registered-agent'] },
  { match: ['ein', 'tax id'], services: ['ein', 'itin', 'usa-account'] },
  { match: ['itin'], services: ['itin', 'ein'] },
  { match: ['boi', 'fincen', 'beneficial'], services: ['boi-report', 'usa-account'] },
  { match: ['trademark'], services: ['trademark-registration'] },
  { match: ['amazon', 'ebay', 'fba'], services: ['usa-account', 'stripe-account', 'ein'] },
  { match: ['uk ', 'limited company', 'ltd'], services: ['usa-account', 'stripe-account'] },
]

// Service → which blog topic terms to show "Related Guides" for. Matched against
// blog title/tags. Uses topical relevance for cross-linking.
export const SERVICE_TO_BLOG_KEYWORDS = {
  'usa-account': ['llc', 'wyoming', 'delaware', 'wise', 'stripe', 'mercury', 'us company'],
  'ein': ['ein', 'tax id', 'irs', 'llc'],
  'itin': ['itin', 'tax filing', 'us tax'],
  'stripe-account': ['stripe', 'payment gateway', 'paypal', 'merchant'],
  'registered-agent': ['llc', 'wyoming', 'delaware', 'compliance'],
  'trademark-registration': ['trademark', 'brand'],
  'annual-report': ['annual report', 'compliance', 'llc'],
  'boi-report': ['boi', 'fincen', 'beneficial ownership'],
  'bookkeeping': ['accounting', 'tax', 'books'],
  'foreign-qualification': ['multi-state', 'expand'],
}

const haystack = (post) =>
  `${post.title || ''} ${(post.tags || []).join(' ')} ${post.slug || ''} ${post.categoryId || ''}`.toLowerCase()

export function pickServicesForBlog(post, max = 3) {
  if (!post) return []
  const text = haystack(post)
  for (const rule of BLOG_TOPIC_TO_SERVICES) {
    if (rule.match.some((kw) => text.includes(kw))) {
      const slugs = rule.services.slice(0, max)
      return slugs.map((s) => SERVICE_CATALOG.find((c) => c.slug === s)).filter(Boolean)
    }
  }
  return SERVICE_CATALOG.filter((s) => ['usa-account', 'ein', 'stripe-account'].includes(s.slug))
}

export function pickBlogsForService(serviceSlug, allPosts = [], max = 3) {
  const keywords = SERVICE_TO_BLOG_KEYWORDS[serviceSlug] || []
  if (!keywords.length || !allPosts.length) return []
  const scored = allPosts.map((p) => {
    const text = haystack(p)
    let score = 0
    for (const kw of keywords) if (text.includes(kw)) score++
    return { p, score }
  })
  return scored
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || (b.p.rawDate || '').localeCompare(a.p.rawDate || ''))
    .slice(0, max)
    .map((x) => x.p)
}
